import { useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { validate as validateCryptoAddress } from "multicoin-address-validator";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { getErrorMessage } from "~/v2/api/hooks";
import {
  useCryptoWalletAPI,
  useGetCryptoAssets,
} from "~/v2/api/hooks/useGetCrypto";
import { useMutationRequest } from "~/v2/api/hooks/useMutationRequest";
import { queryKeys } from "~/v2/api/queryKeys";
import {
  type CreateWhitelistAddressArgs,
  createWhitelistedAddress,
} from "~/v2/api/rest/cryptoWallet";
import { Icon } from "~/v2/components/AssetIcon/AssetIcon";
import { getCryptoAssetName, parseCryptoAssetSymbol } from "../util";

interface UseLegacyAddWhitelistAddressProps {
  accountId: string;
  isFetchingEnabled: boolean;
}

export const useLegacyAddWhitelistAddress = ({
  accountId,
  isFetchingEnabled,
}: UseLegacyAddWhitelistAddressProps) => {
  const queryClient = useQueryClient();
  const cryptoWalletAxiosInstance = useCryptoWalletAPI();
  const assets = useGetCryptoAssets({
    accountId,
    options: { enabled: isFetchingEnabled },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [symbol, setSymbol] = useState("");
  const [symbolError, setSymbolError] = useState("");

  const symbolOptions = useMemo(
    () =>
      (assets.data ?? []).map((asset) => {
        const assetName = getCryptoAssetName(asset.symbol ?? "");
        const iconKey = `${assetName}/USD`;

        return {
          value: asset.symbol ?? "",
          label: asset.symbol ?? "",
          icon: asset.symbol ? (
            <Icon name={iconKey} className="h-auto w-auto" />
          ) : null,
        };
      }),
    [assets.data],
  );

  const reset = () => {
    setAddress("");
    setAddressError("");
    setSymbol("");
    setSymbolError("");
  };

  const onOpen = () => {
    reset();
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    reset();
  };

  const createWhitelistAddressMutation = useMutationRequest(
    async (args: CreateWhitelistAddressArgs) =>
      createWhitelistedAddress(cryptoWalletAxiosInstance, accountId, args),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.crypto.whitelists(accountId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.crypto.approvedWhitelists(accountId),
        });
        toastSuccess("Whitelist address created successfully");
        onClose();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to create whitelist address: ${errorMessage}`);
        setAddressError(errorMessage);
      },
    },
  );

  const onAddressChange = (nextAddress: string) => {
    setAddress(nextAddress);
    if (addressError) {
      setAddressError("");
    }
  };

  const onSymbolChange = (nextSymbol: string) => {
    setSymbol(nextSymbol);
    if (symbolError) {
      setSymbolError("");
    }
  };

  const onSubmit = () => {
    const hasAddress = !!address.trim();
    const hasSymbol = !!symbol;

    if (!hasAddress) {
      setAddressError("Address is required");
    }
    if (!hasSymbol) {
      setSymbolError("Crypto asset is required");
    }
    if (!hasAddress || !hasSymbol) {
      return;
    }

    const { chain, asset } = parseCryptoAssetSymbol(symbol);

    const isAddressValid = (() => {
      try {
        return validateCryptoAddress(address, chain);
      } catch {
        return false;
      }
    })();

    if (!isAddressValid) {
      setAddressError(
        `The address you entered is not compatible with the ${chain} network. Please provide a valid ${chain} address.`,
      );
      return;
    }

    createWhitelistAddressMutation.mutate({
      chain,
      asset,
      address,
    });
  };

  return {
    isOpen,
    onOpen,
    onClose,
    symbolOptions,
    symbol,
    symbolError,
    onSymbolChange,
    address,
    addressError,
    onAddressChange,
    onSubmit,
    isSubmitLoading: createWhitelistAddressMutation.isPending,
    isSubmitDisabled: !address.trim() || !symbol,
  };
};

export type UseLegacyAddWhitelistAddressReturn = ReturnType<
  typeof useLegacyAddWhitelistAddress
>;
